import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import CryptoSelector from './crypto_selector';
import PriceChart from './price_chart';
import OrderBook from './orderBook';
import OrderForm from './order_form';
import OrdersPanel from './orders_panel';
import TradeHistory from './trade_history';
import RFMResponseForm from './RFMResponseForm';
import MatchingResultModal from './MatchingResultModal';
import MatchingProgressModal from './MatchingProgressModal';
import HelpModal from './HelpModal';
import { fetchEnclavePublicKey, encryptForEnclave } from './enclaveEncryption';

function Dashboard() {
  const { user, logout, token } = useAuth();
  const [selectedPair, setSelectedPair] = useState({
    symbol: 'BTCUSDT',
    baseAsset: 'BTC',
    quoteAsset: 'USDT',
    displayName: 'BTC-USDT',
    coinId: 'bitcoin'
  });

  const [tickerData, setTickerData] = useState(null);
  const [currentPrice, setCurrentPrice] = useState(null);
  const [lastTradePrice, setLastTradePrice] = useState(null);

  // Matching request state
  const [activeMatchingRequest, setActiveMatchingRequest] = useState(null);
  const [showRFMForm, setShowRFMForm] = useState(false);

  // Matching result state
  const [matchingResult, setMatchingResult] = useState(null);
  const [showMatchingResult, setShowMatchingResult] = useState(false);

  // Matching progress state (for initiator)
  const [showMatchingProgress, setShowMatchingProgress] = useState(false);
  const [matchingProgressData, setMatchingProgressData] = useState(null);

  // Responder waiting state (after submitting response)
  const [showResponderWaiting, setShowResponderWaiting] = useState(false);
  const [responderWaitingData, setResponderWaitingData] = useState(null);

  // Pending order data for initiator — keyed by requestId to handle concurrent RFQs.
  // Stage: single slot (no requestId yet) → promoted to Map in handleMatchingRequestSuccess.
  const pendingOrderDataRef = useRef(null);       // staging slot before requestId is known
  const pendingOrdersByRequestId = useRef(new Map()); // keyed by requestId after HTTP succeeds
  const [notificationPermission, setNotificationPermission] = useState(
    'Notification' in window ? Notification.permission : 'unsupported'
  );
  const wsRef = useRef(null);

  // Connection status tracking
  const [frontendWsStatus, setFrontendWsStatus] = useState('disconnected');
  const [backendWsStatus, setBackendWsStatus] = useState('unknown');

  // Reconnection tracking
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  const maxReconnectAttempts = 10;
  const baseReconnectDelay = 1000; // 1 second

  // Help modal state
  const [showHelp, setShowHelp] = useState(false);

  // Session superseded state (when another tab takes over)
  const [sessionSuperseded, setSessionSuperseded] = useState(false);

  // Check notification permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      console.log('[Dashboard] Current notification permission:', Notification.permission);
      setNotificationPermission(Notification.permission);
    }
  }, []);

  // Restore matching progress state from localStorage on mount
  useEffect(() => {
    try {
      const savedProgress = localStorage.getItem('matchingProgress');
      if (savedProgress) {
        const progress = JSON.parse(savedProgress);
        const now = Date.now();
        const elapsed = (now - progress.startTime) / 1000;
        const remaining = progress.matchingWindow - elapsed;

        // Only restore if there's still time remaining (with 5 second buffer for processing)
        if (remaining > -5) {
          console.log('[Dashboard] Restoring matching progress from localStorage:', progress);
          setMatchingProgressData(progress);
          setShowMatchingProgress(true);
        } else {
          // Expired, clear it
          console.log('[Dashboard] Matching progress expired, clearing localStorage');
          localStorage.removeItem('matchingProgress');
        }
      }

      const savedResponderWaiting = localStorage.getItem('responderWaiting');
      if (savedResponderWaiting) {
        const waiting = JSON.parse(savedResponderWaiting);
        const now = Date.now();
        const elapsed = (now - waiting.startTime) / 1000;
        const remaining = waiting.timeRemaining - elapsed;

        if (remaining > -5) {
          console.log('[Dashboard] Restoring responder waiting from localStorage:', waiting);
          setResponderWaitingData(waiting);
          setShowResponderWaiting(true);
        } else {
          localStorage.removeItem('responderWaiting');
        }
      }
    } catch (e) {
      console.error('[Dashboard] Error restoring matching state:', e);
    }
  }, []);

  // Save matching progress to localStorage when it changes
  useEffect(() => {
    if (showMatchingProgress && matchingProgressData) {
      console.log('[Dashboard] Saving matching progress to localStorage');
      localStorage.setItem('matchingProgress', JSON.stringify(matchingProgressData));
    } else {
      localStorage.removeItem('matchingProgress');
    }
  }, [showMatchingProgress, matchingProgressData]);

  // Save responder waiting to localStorage when it changes
  useEffect(() => {
    if (showResponderWaiting && responderWaitingData) {
      console.log('[Dashboard] Saving responder waiting to localStorage');
      localStorage.setItem('responderWaiting', JSON.stringify(responderWaitingData));
    } else {
      localStorage.removeItem('responderWaiting');
    }
  }, [showResponderWaiting, responderWaitingData]);

  // Function to request notification permission
  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      try {
        const permission = await Notification.requestPermission();
        console.log('[Dashboard] Notification permission result:', permission);
        setNotificationPermission(permission);

        if (permission === 'granted') {
          // Show a test notification
          new Notification('Notifications Enabled!', {
            body: 'You will now receive alerts for matching requests',
            icon: '/favicon.ico'
          });
        }
      } catch (error) {
        console.error('[Dashboard] Error requesting notification permission:', error);
      }
    }
  };

  // Handle own matching request - encrypt and submit order data
  // Note: Progress bar is shown from HTTP callback (handleMatchingRequestSuccess),
  // this function just handles the encryption and submission
  const handleOwnMatchingRequest = async (message) => {
    try {
      const { ticker, requestId, matching_window } = message;

      console.log('[Dashboard] Encrypting order data for own matching request');
      console.log('[Dashboard] Ticker:', ticker, 'RequestID:', requestId);

      // Look up order data by requestId (preferred) or fall back to staging slot
      const storedOrderData = pendingOrdersByRequestId.current.get(requestId) || pendingOrderDataRef.current;
      if (!storedOrderData) {
        console.error('[Dashboard] No pending order data found for requestId:', requestId);
        return;
      }
      // Consume immediately before any awaits to prevent concurrent-RFQ overwrites
      pendingOrdersByRequestId.current.delete(requestId);
      if (pendingOrderDataRef.current === storedOrderData) pendingOrderDataRef.current = null;

      console.log('[Dashboard] Using pending order data for', requestId, ':', storedOrderData);

      // Determine if this is a bid (BUY) or offer (SELL)
      const isBid = storedOrderData.side === 'BUY';
      const isOffer = storedOrderData.side === 'SELL';

      // Build the order data structure
      const orderData = {
        requestId: requestId,
        ticker: ticker,
        timestamp: Date.now(),
        isBid: isBid,
        isOffer: isOffer,
        // Include bid fields only if it's a bid
        ...(isBid && {
          bidQty: storedOrderData.qty,
          bidPrice: storedOrderData.price
        }),
        // Include offer fields only if it's an offer
        ...(isOffer && {
          offerQty: storedOrderData.qty,
          offerPrice: storedOrderData.price
        })
      };

      console.log('[Dashboard] Order data to encrypt:', orderData);

      // Fetch enclave public key
      console.log('[Dashboard] Fetching enclave public key...');
      const publicKey = await fetchEnclavePublicKey();
      console.log('[Dashboard] ✓ Public key fetched');

      // Encrypt the order data
      console.log('[Dashboard] Encrypting order data...');
      const encryptedPayload = await encryptForEnclave(orderData, publicKey);
      console.log('[Dashboard] ✓ Order data encrypted');

      // Submit to backend (use authToken from localStorage to avoid closure issues)
      const authToken = localStorage.getItem('authToken');
      console.log('[Dashboard] Submitting encrypted payload to /api/enclave/encrypt...');

      const response = await fetch('/api/enclave/encrypt', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          encrypted_payload: encryptedPayload,
          submission_type: 'matching_order',
          request_id: requestId
        })
      });

      const result = await response.json();

      if (result.success) {
        console.log('[Dashboard] ========================================');
        console.log('[Dashboard] ✓ ORDER ENCRYPTED AND SUBMITTED!');
        console.log('[Dashboard] Submission ID:', result.data.submission_id);
        console.log('[Dashboard] Created at:', result.data.created_at);
        console.log('[Dashboard] ========================================');
      } else {
        console.error('[Dashboard] Failed to submit encrypted order:', result.error);
      }
    } catch (error) {
      console.error('[Dashboard] Error handling own matching request:', error);
      console.error('[Dashboard] Error details:', error.message);
    }
  };

  // Callback for OrderForm - stores order data when initiating a matching request
  const handleMatchingRequestSubmit = (orderData) => {
    console.log('[Dashboard] Storing pending order data:', orderData);
    pendingOrderDataRef.current = orderData;
  };

  // Callback for OrderForm - called when matching request HTTP call succeeds
  // This ensures the progress bar shows immediately without waiting for WebSocket
  const handleMatchingRequestSuccess = (matchingData) => {
    console.log('[Dashboard] Matching request succeeded, showing progress bar:', matchingData);
    // Promote staged order data to the requestId-keyed map now that we have the requestId
    if (pendingOrderDataRef.current && matchingData.requestId) {
      pendingOrdersByRequestId.current.set(matchingData.requestId, pendingOrderDataRef.current);
      pendingOrderDataRef.current = null;
      console.log('[Dashboard] Promoted pending order data for requestId:', matchingData.requestId);
    }
    setMatchingProgressData({
      ticker: matchingData.ticker,
      matchingWindow: matchingData.matchingWindow,
      startTime: matchingData.startTime,
      requestId: matchingData.requestId
    });
    setShowMatchingProgress(true);
  };

  // Fetch initial ticker data
  useEffect(() => {
    if (selectedPair?.coinId) {
      console.log(`Token changed to ${selectedPair.coinId}, fetching new price data...`);
      // Reset all price-related state immediately to prevent stale data
      setCurrentPrice(null);
      setTickerData(null);
      setLastTradePrice(null); // Clear old token's trade price
      fetchTickerData(selectedPair.coinId);
    }
  }, [selectedPair]);

  // Polling for price updates (CoinGecko doesn't have WebSocket in free tier)
  useEffect(() => {
    if (!selectedPair?.coinId) return;

    // Update every 30 seconds
    const interval = setInterval(() => {
      fetchTickerData(selectedPair.coinId);
    }, 30000);

    return () => clearInterval(interval);
  }, [selectedPair]);

  const fetchTickerData = async (coinId) => {
    try {
      console.log(`Fetching ticker data for ${coinId}...`);
      const response = await fetch(`/api/crypto/ticker/${coinId}`);
      const result = await response.json();

      console.log(`Ticker response for ${coinId}:`, result);

      if (result.success) {
        setTickerData(result.data);
        setCurrentPrice(result.data.lastPrice);
        console.log(`Updated price for ${coinId}: $${result.data.lastPrice}`);
      } else {
        console.error(`Failed to fetch ticker for ${coinId}:`, result.error);
      }
    } catch (error) {
      console.error(`Error fetching ticker data for ${coinId}:`, error);
    }
  };

  // Function to fetch missed matching results on connect
  const fetchMissedMatchingResults = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;

      // Get the last seen result ID to avoid showing duplicates
      const lastSeenResultId = localStorage.getItem('lastSeenMatchingResultId');

      console.log('[Dashboard] Fetching missed matching results...');
      const response = await fetch('/api/matching/my-results', {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      const data = await response.json();
      if (data.success && data.results && data.results.length > 0) {
        console.log(`[Dashboard] Found ${data.results.length} matching result(s)`);

        // Show the most recent result only if it hasn't been seen
        const mostRecent = data.results[0];

        // Skip if we've already seen this result
        if (mostRecent.request_id && mostRecent.request_id === lastSeenResultId) {
          console.log('[Dashboard] Most recent result already seen, skipping');
          return;
        }

        // Only show if it's within the last 30 minutes (missed during disconnect or page navigation)
        const resultTime = new Date(mostRecent.processed_at).getTime();
        const now = Date.now();
        const thirtyMinutes = 30 * 60 * 1000;

        if (now - resultTime < thirtyMinutes) {
          console.log('[Dashboard] Recent missed result found, showing modal:', mostRecent);

          // Mark as seen before showing
          localStorage.setItem('lastSeenMatchingResultId', mostRecent.request_id);

          setMatchingResult(mostRecent);
          setShowMatchingResult(true);

          // Show notification
          if ('Notification' in window && Notification.permission === 'granted') {
            const isMatched = mostRecent.matched === true;
            new Notification(isMatched ? '✅ Match Found!' : '❌ No Match', {
              body: `${mostRecent.ticker?.toUpperCase()} - ${mostRecent.reason || (isMatched ? 'Your order was matched!' : 'No match found')}`,
              icon: '/favicon.ico',
              tag: `result-${mostRecent.request_id}`,
              requireInteraction: true
            });
          }
        } else {
          console.log('[Dashboard] Result is older than 2 minutes, skipping');
        }
      }
    } catch (error) {
      console.error('[Dashboard] Error fetching missed matching results:', error);
    }
  };

  // WebSocket connection for matching requests with auto-reconnect
  useEffect(() => {
    console.log('[Dashboard] User object:', user);
    console.log('[Dashboard] User ID:', user?.userId);

    if (!user?.userId) {
      console.log('[Dashboard] No userId found, skipping WebSocket connection');
      return;
    }

    let isCleaningUp = false;

    const connectWebSocket = () => {
      if (isCleaningUp || sessionSuperseded) {
        console.log('[Dashboard] Skipping reconnect (cleanup or superseded)');
        return;
      }

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}`;

      console.log('[Dashboard] Connecting to WebSocket:', wsUrl);
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('[Dashboard] WebSocket connected');
        setFrontendWsStatus('connected');
        reconnectAttemptsRef.current = 0; // Reset reconnect counter on success

        // Get liquidityProviderType from settings
        let participantType = null;
        try {
          const settings = JSON.parse(localStorage.getItem('tradingSettings') || '{}');
          const lpType = settings.liquidityProviderType;
          // Only set participantType if user is offering liquidity
          if (lpType && lpType !== 'Not Offering Liquidity') {
            participantType = lpType;
          }
          console.log('[Dashboard] Liquidity provider type from settings:', lpType, '-> participantType:', participantType);
        } catch (e) {
          console.warn('[Dashboard] Could not read trading settings:', e);
        }

        // Authenticate with userId, email, and participantType
        ws.send(JSON.stringify({
          type: 'auth',
          userId: user.userId,
          email: user.email,
          participantType: participantType
        }));

        // Fetch any missed matching results
        fetchMissedMatchingResults();

        // Keepalive: send a ping every 30s so cloud infra doesn't drop the idle connection
        const keepaliveInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping', userId: user.userId }));
          }
        }, 30000);
        ws._keepaliveInterval = keepaliveInterval;
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          console.log('[Dashboard] Received message:', message);

          // Handle matching requests
          if (message.type === 'matching_request') {
            console.log('[Dashboard] ========================================');
            console.log('[Dashboard] MATCHING REQUEST RECEIVED!');
            console.log('[Dashboard] Message:', message);
            console.log('[Dashboard] Current user ID:', user.userId);
            console.log('[Dashboard] Initiator user ID:', message.initiatorUserId);
            console.log('[Dashboard] ========================================');

            // If this user initiated the request, encrypt and submit order data
            if (message.initiatorUserId === user.userId) {
              console.log('[Dashboard] This is OUR matching request - encrypting order data...');
              // Progress bar should already be showing from HTTP callback, but ensure it's visible
              if (!showMatchingProgress) {
                console.log('[Dashboard] Progress bar not showing, triggering from WebSocket as fallback');
                setMatchingProgressData({
                  ticker: message.ticker,
                  matchingWindow: message.matching_window || 30,
                  startTime: Date.now(),
                  requestId: message.requestId
                });
                setShowMatchingProgress(true);
              }
              handleOwnMatchingRequest(message);
              return;
            }

            console.log('[Dashboard] ✓ This is from another user, processing...');
            console.log('[Dashboard] Notification permission:', Notification.permission);

            // Show browser notification
            if ('Notification' in window && Notification.permission === 'granted') {
              console.log('[Dashboard] 📢 Creating browser notification...');
              const uniqueTag = `matching-${message.requestId}`;
              const notification = new Notification('🔔 New Matching Request!', {
                body: `${message.ticker} - ${message.execution_mode ? 'Fast' : 'Secure'} Mode\nWindow: ${message.matching_window}s`,
                icon: '/favicon.ico',
                tag: uniqueTag,
                requireInteraction: true,
                badge: '/favicon.ico'
              });

              notification.onclick = () => {
                console.log('[Dashboard] Notification clicked!');
                window.focus();
                notification.close();
              };

              console.log('[Dashboard] ✓ Browser notification created');
            } else {
              console.log('[Dashboard] ⚠️ Browser notification NOT created. Permission:', Notification.permission);
            }

            console.log('[Dashboard] 📋 Setting form state...');
            setActiveMatchingRequest(message);
            setShowRFMForm(true);
            console.log('[Dashboard] ✓ Form state updated. showRFMForm should now be true');
          }

          // Handle matching results
          if (message.type === 'matching_result') {
            console.log('[Dashboard] ========================================');
            console.log('[Dashboard] MATCHING RESULT RECEIVED!');
            console.log('[Dashboard] Result:', message.data);
            console.log('[Dashboard] ========================================');

            // Show browser notification
            if ('Notification' in window && Notification.permission === 'granted') {
              const isMatched = message.data.matched === true;
              try {
                const n = new Notification(isMatched ? 'Match Found!' : 'No Match', {
                  body: `${message.data.ticker?.toUpperCase()} - ${message.data.reason || (isMatched ? 'Your order was matched!' : 'No match found')}`,
                  icon: '/favicon.ico',
                  tag: `result-${message.data.request_id}`,
                  requireInteraction: true
                });
                console.log('[Dashboard] OS notification created:', n);
                n.onerror = (e) => console.error('[Dashboard] Notification error:', e);
              } catch (e) {
                console.error('[Dashboard] Notification creation failed:', e);
              }
            } else {
              console.warn('[Dashboard] Notification skipped — permission:', 'Notification' in window ? Notification.permission : 'unsupported');
            }

            // Close RFM form if open (matching is complete)
            setShowRFMForm(false);
            setActiveMatchingRequest(null);

            // Close matching progress modal (for initiator)
            setShowMatchingProgress(false);
            setMatchingProgressData(null);

            // Close responder waiting modal
            setShowResponderWaiting(false);
            setResponderWaitingData(null);

            // Show the matching result modal
            if (message.data?.request_id) {
              localStorage.setItem('lastSeenMatchingResultId', message.data.request_id);
            }
            setMatchingResult(message.data);
            setShowMatchingResult(true);
          }

          // Handle session superseded (another tab took over)
          if (message.type === 'session_superseded') {
            console.log('[Dashboard] Session superseded by another tab');
            setSessionSuperseded(true);
            // Don't try to reconnect if superseded
            if (reconnectTimeoutRef.current) {
              clearTimeout(reconnectTimeoutRef.current);
              reconnectTimeoutRef.current = null;
            }
          }
        } catch (error) {
          console.error('[Dashboard] Error parsing WebSocket message:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('[Dashboard] WebSocket error:', error);
        setFrontendWsStatus('error');
      };

      ws.onclose = () => {
        console.log('[Dashboard] WebSocket disconnected');
        if (ws._keepaliveInterval) clearInterval(ws._keepaliveInterval);
        setFrontendWsStatus('disconnected');

        // Attempt to reconnect unless cleaning up or superseded
        if (!isCleaningUp && !sessionSuperseded && reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = Math.min(baseReconnectDelay * Math.pow(2, reconnectAttemptsRef.current), 30000);
          reconnectAttemptsRef.current += 1;
          console.log(`[Dashboard] Scheduling reconnect attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts} in ${delay}ms`);
          setFrontendWsStatus('reconnecting');

          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('[Dashboard] Attempting reconnect...');
            connectWebSocket();
          }, delay);
        } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          console.log('[Dashboard] Max reconnect attempts reached');
          setFrontendWsStatus('failed');
        }
      };

      wsRef.current = ws;
    };

    connectWebSocket();

    return () => {
      isCleaningUp = true;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        if (wsRef.current._keepaliveInterval) clearInterval(wsRef.current._keepaliveInterval);
        if (wsRef.current.readyState === WebSocket.OPEN) wsRef.current.close();
      }
    };
  }, [user, sessionSuperseded]);

  // Poll backend connection status
  useEffect(() => {
    const checkBackendStatus = async () => {
      try {
        const response = await fetch('/debug/connections');
        const data = await response.json();

        // Find this user's backend connection
        const myBackendConn = data.backend.connections.find(
          conn => conn.userId === user?.userId
        );

        if (myBackendConn) {
          setBackendWsStatus(myBackendConn.isConnected ? 'connected' : 'disconnected');
        } else {
          setBackendWsStatus('disconnected');
        }
      } catch (error) {
        console.error('[Dashboard] Error checking backend status:', error);
        setBackendWsStatus('unknown');
      }
    };

    if (user?.userId) {
      checkBackendStatus();
      const interval = setInterval(checkBackendStatus, 1000); // Check every second
      return () => clearInterval(interval);
    }
  }, [user]);

  const handlePairChange = (newPair) => {
    console.log('Changing pair to:', newPair);
    setLastTradePrice(null); // Clear stale price before new pair renders
    setSelectedPair(newPair);
  };

  const handleRFMClose = () => {
    setShowRFMForm(false);
    setActiveMatchingRequest(null);
  };

  const handleRFMSubmit = (response) => {
    console.log('[Dashboard] Matching response submitted:', response);

    // If this is a successful submission notification, show the waiting progress
    if (response.type === 'matching_response_submitted' && response.waitingData) {
      console.log('[Dashboard] Showing responder waiting progress:', response.waitingData);
      setResponderWaitingData(response.waitingData);
      setShowResponderWaiting(true);
      // Close the form since submission is complete
      setShowRFMForm(false);
      setActiveMatchingRequest(null);
    }
  };

  const handleMatchingResultClose = () => {
    setShowMatchingResult(false);
    setMatchingResult(null);
  };

  return (
    <div className="dashboard-container">
      {/* Combined Header Bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 16px',
        backgroundColor: '#1a1a1a',
        borderBottom: '1px solid #2a2a2a',
        gap: '12px',
        flexWrap: 'nowrap'
      }}>
        {/* Left: User info + Status */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
          <span style={{ color: '#888', fontSize: '12px', whiteSpace: 'nowrap' }}>{user?.email}</span>

          {/* Connection dots */}
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            <div
              title={`Frontend: ${frontendWsStatus}${frontendWsStatus === 'reconnecting' ? ` (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})` : ''}`}
              style={{
                width: '8px', height: '8px', borderRadius: '50%',
                backgroundColor:
                  frontendWsStatus === 'connected' ? '#26a69a' :
                  frontendWsStatus === 'reconnecting' ? '#f5a623' :
                  (frontendWsStatus === 'error' || frontendWsStatus === 'failed' || frontendWsStatus === 'disconnected') ? '#ef5350' : '#666',
                animation: frontendWsStatus === 'reconnecting' ? 'pulse 1s infinite' : 'none'
              }}
            ></div>
            <div title={`Backend: ${backendWsStatus}`} style={{
              width: '8px', height: '8px', borderRadius: '50%',
              backgroundColor: backendWsStatus === 'connected' ? '#26a69a' : backendWsStatus === 'disconnected' ? '#ef5350' : '#666'
            }}></div>
          </div>
          {/* Reconnecting text indicator */}
          {frontendWsStatus === 'reconnecting' && (
            <span style={{ color: '#f5a623', fontSize: '10px', marginLeft: '4px' }}>Reconnecting...</span>
          )}
          {frontendWsStatus === 'failed' && (
            <span style={{ color: '#ef5350', fontSize: '10px', marginLeft: '4px' }}>Connection failed - please refresh</span>
          )}

          {/* Notification status - compact */}
          {notificationPermission === 'denied' && (
            <span title="Click the lock icon in the address bar to enable" style={{ color: '#ef5350', fontSize: '14px', cursor: 'help' }}>🔕</span>
          )}
          {notificationPermission === 'default' && (
            <button onClick={(e) => { e.preventDefault(); requestNotificationPermission(); }}
              title="Enable notifications"
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', padding: '2px' }}>🔔</button>
          )}
          {notificationPermission === 'granted' && (
            <span title="Notifications enabled" style={{ color: '#26a69a', fontSize: '14px' }}>🔔</span>
          )}
        </div>

        {/* Center: Price info (inline from PriceHeader) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1, justifyContent: 'center' }}>
          <CryptoSelector selectedPair={selectedPair} onPairChange={handlePairChange} />

          <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#fff', fontSize: '14px', fontWeight: '600' }}>
                {tickerData?.lastPrice?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
              </div>
              <div style={{ color: '#666', fontSize: '10px' }}>Price</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: tickerData?.priceChangePercent >= 0 ? '#26a69a' : '#ef5350', fontSize: '13px', fontWeight: '500' }}>
                {tickerData?.priceChangePercent >= 0 ? '+' : ''}{tickerData?.priceChangePercent?.toFixed(2) || '0.00'}%
              </div>
              <div style={{ color: '#666', fontSize: '10px' }}>24h</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#ccc', fontSize: '12px' }}>
                {tickerData?.volume?.toLocaleString('en-US', { maximumFractionDigits: 0 }) || '0'}
              </div>
              <div style={{ color: '#666', fontSize: '10px' }}>Vol</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#26a69a', fontSize: '12px' }}>{tickerData?.highPrice?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}</div>
              <div style={{ color: '#666', fontSize: '10px' }}>High</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#ef5350', fontSize: '12px' }}>{tickerData?.lowPrice?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}</div>
              <div style={{ color: '#666', fontSize: '10px' }}>Low</div>
            </div>
          </div>
        </div>

        {/* Right: Contact + Help + Logout */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <a href="https://t.me/ocuros_support" target="_blank" rel="noopener noreferrer" title="Chat on Telegram"
            style={{ background: 'none', border: 'none', color: '#26a69a', cursor: 'pointer', fontSize: '14px', padding: '4px 8px', textDecoration: 'none' }}>
            💬
          </a>
          <button onClick={() => setShowHelp(true)} title="Help"
            style={{ background: 'none', border: 'none', color: '#26a69a', cursor: 'pointer', fontSize: '13px', padding: '4px 8px' }}>?</button>
          <button onClick={logout}
            style={{ background: 'none', border: '1px solid #444', color: '#999', borderRadius: '4px', padding: '4px 10px', fontSize: '11px', cursor: 'pointer' }}>
            Logout
          </button>
        </div>
      </div>

      <div className="trading-grid">
        {/* Left Column - Order Book */}
        <div className="order-book-panel">
          <OrderBook coinId={selectedPair?.coinId} />
        </div>

        {/* Center - Chart + Order Form */}
        <div className="chart-panel">
          <PriceChart
            coinId={selectedPair?.coinId}
            currentPrice={currentPrice}
          />
          <OrderForm selectedPair={selectedPair} lastTradePrice={lastTradePrice} onMatchingRequestSubmit={handleMatchingRequestSubmit} onMatchingRequestSuccess={handleMatchingRequestSuccess} wsClient={wsRef.current} userId={user?.userId} isConnected={frontendWsStatus === 'connected' && backendWsStatus === 'connected'} />
        </div>

        {/* Right Column - Trade History */}
        <div className="trade-history-panel">
          <TradeHistory coinId={selectedPair?.coinId} onLastPriceUpdate={setLastTradePrice} />
        </div>
      </div>

      {/* Matching Request Form Popup */}
      {showRFMForm && activeMatchingRequest && (
        <RFMResponseForm
          matchingRequest={activeMatchingRequest}
          onClose={handleRFMClose}
          onSubmit={handleRFMSubmit}
          wsClient={wsRef.current}
        />
      )}

      {/* Matching Result Modal */}
      {showMatchingResult && matchingResult && (
        <MatchingResultModal
          result={matchingResult}
          onClose={handleMatchingResultClose}
        />
      )}

      {/* Matching Progress Modal (for initiator) */}
      {showMatchingProgress && matchingProgressData && (
        <MatchingProgressModal
          isOpen={showMatchingProgress}
          ticker={matchingProgressData.ticker}
          matchingWindow={matchingProgressData.matchingWindow}
          startTime={matchingProgressData.startTime}
          onComplete={() => {
            // Progress complete, wait for result from server
            console.log('[Dashboard] Matching window complete, waiting for result...');
          }}
        />
      )}

      {/* Responder Waiting Modal (after submitting response) */}
      {showResponderWaiting && responderWaitingData && (
        <MatchingProgressModal
          isOpen={showResponderWaiting}
          ticker={responderWaitingData.ticker}
          matchingWindow={responderWaitingData.timeRemaining}
          startTime={responderWaitingData.startTime}
          title="Response Submitted"
          description="Waiting for matching window to close and results to be computed"
          onComplete={() => {
            console.log('[Dashboard] Responder waiting window complete, waiting for result...');
          }}
        />
      )}

      {/* Help Modal */}
      {showHelp && (
        <HelpModal onClose={() => setShowHelp(false)} />
      )}

      {/* Session Superseded Overlay */}
      {sessionSuperseded && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: '#1a1a2e',
            borderRadius: '12px',
            padding: '40px',
            textAlign: 'center',
            border: '1px solid #ef5350',
            maxWidth: '450px'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
            <h2 style={{ color: '#ef5350', margin: '0 0 16px 0', fontSize: '24px' }}>
              Session Taken Over
            </h2>
            <p style={{ color: '#ccc', margin: 0, lineHeight: '1.6' }}>
              This tab has been superseded by another browser tab.
              Please close this tab and continue in your other tab.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
